import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { motion, AnimatePresence } from 'framer-motion';
import './ManageDemoTest.css';

const API_URL = 'https://test-series-backend-dyfc.onrender.com/api';

const ManageBlogs = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  
  const [blogData, setBlogData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    author: '',
    category: 'JEE',
    tags: [],
    featuredImage: '',
    isPublished: false
  });

  const [tagInput, setTagInput] = useState('');

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API_URL}/blogs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBlogs(data.blogs || []);
    } catch (error) {
      toast.error('Failed to fetch blogs');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBlog = async () => {
    try {
      if (!blogData.title.trim()) {
        toast.error('Please enter a blog title');
        return;
      }

      if (!blogData.content.trim()) {
        toast.error('Please enter blog content');
        return;
      }

      const token = localStorage.getItem('token');
      
      // Auto-generate slug from title if not provided
      const slug = blogData.slug || blogData.title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');

      const payload = {
        ...blogData,
        slug
      };

      if (editingBlog) {
        await axios.put(`${API_URL}/blogs/${editingBlog._id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Blog updated successfully!');
      } else {
        await axios.post(`${API_URL}/blogs`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Blog created successfully!');
      }

      setShowModal(false);
      resetForm();
      fetchBlogs();
    } catch (error) {
      console.error('Blog error:', error);
      toast.error(error.response?.data?.message || 'Failed to save blog');
    }
  };

  const handleEditBlog = (blog) => {
    setBlogData({
      title: blog.title,
      slug: blog.slug,
      excerpt: blog.excerpt || '',
      content: blog.content,
      author: blog.author || '',
      category: blog.category || 'JEE',
      tags: blog.tags || [],
      featuredImage: blog.featuredImage || '',
      isPublished: blog.isPublished || false
    });
    setEditingBlog(blog);
    setShowModal(true);
  };

  const handleDeleteBlog = async (id) => {
    if (!window.confirm('Are you sure you want to delete this blog?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/blogs/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Blog deleted successfully');
      fetchBlogs();
    } catch (error) {
      toast.error('Failed to delete blog');
      console.error(error);
    }
  };

  const resetForm = () => {
    setBlogData({
      title: '',
      slug: '',
      excerpt: '',
      content: '',
      author: '',
      category: 'JEE',
      tags: [],
      featuredImage: '',
      isPublished: false
    });
    setEditingBlog(null);
    setTagInput('');
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !blogData.tags.includes(tagInput.trim())) {
      setBlogData({
        ...blogData,
        tags: [...blogData.tags, tagInput.trim()]
      });
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setBlogData({
      ...blogData,
      tags: blogData.tags.filter(tag => tag !== tagToRemove)
    });
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setBlogData({ ...blogData, featuredImage: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="manage-demo-test">
      <div className="header-section">
        <h1>📝 Manage Blogs</h1>
        <button className="btn btn-success" onClick={() => setShowModal(true)}>
          + Create New Blog Post
        </button>
      </div>

      {loading ? (
        <div className="loading">Loading blogs...</div>
      ) : (
        <div className="tests-grid">
          {blogs.length === 0 ? (
            <div className="no-tests">
              <p>No blog posts yet. Create your first one!</p>
            </div>
          ) : (
            blogs.map((blog) => (
              <motion.div
                key={blog._id}
                className="test-card"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                whileHover={{ scale: 1.02 }}
              >
                {blog.featuredImage && (
                  <div style={{ 
                    width: '100%', 
                    height: '200px', 
                    overflow: 'hidden',
                    borderRadius: '8px 8px 0 0'
                  }}>
                    <img 
                      src={blog.featuredImage} 
                      alt={blog.title}
                      style={{ 
                        width: '100%', 
                        height: '100%', 
                        objectFit: 'cover' 
                      }}
                    />
                  </div>
                )}
                
                <div className="test-card-header">
                  <h3>{blog.title}</h3>
                  <span className={`badge ${blog.isPublished ? 'badge-success' : 'badge-danger'}`}>
                    {blog.isPublished ? 'Published' : 'Draft'}
                  </span>
                </div>
                
                <div className="test-card-body">
                  <div className="test-info">
                    <p><strong>Category:</strong> {blog.category}</p>
                    <p><strong>Author:</strong> {blog.author || 'Admin'}</p>
                    <p><strong>Slug:</strong> {blog.slug}</p>
                    {blog.excerpt && <p><strong>Excerpt:</strong> {blog.excerpt.substring(0, 100)}...</p>}
                    {blog.tags && blog.tags.length > 0 && (
                      <div style={{ marginTop: '10px' }}>
                        <strong>Tags:</strong>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', marginTop: '5px' }}>
                          {blog.tags.map((tag, idx) => (
                            <span key={idx} className="badge" style={{ fontSize: '12px' }}>
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    <p style={{ marginTop: '10px', fontSize: '12px', color: '#666' }}>
                      Created: {new Date(blog.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                </div>

                <div className="test-card-actions">
                  <button className="btn btn-primary btn-sm" onClick={() => handleEditBlog(blog)}>
                    Edit
                  </button>
                  <button className="btn btn-danger btn-sm" onClick={() => handleDeleteBlog(blog._id)}>
                    Delete
                  </button>
                </div>
              </motion.div>
            ))
          )}
        </div>
      )}

      {/* Create/Edit Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowModal(false)}
          >
            <motion.div
              className="modal-content large-modal"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              style={{ maxHeight: '90vh', overflowY: 'auto' }}
            >
              <div className="modal-header">
                <h2>{editingBlog ? 'Edit Blog Post' : 'Create New Blog Post'}</h2>
                <button className="close-btn" onClick={() => setShowModal(false)}>×</button>
              </div>

              <div className="modal-body">
                <div className="form-section">
                  <h3>📝 Blog Information</h3>
                  
                  <div className="form-group">
                    <label>Title *</label>
                    <input
                      type="text"
                      value={blogData.title}
                      onChange={(e) => setBlogData({ ...blogData, title: e.target.value })}
                      placeholder="Enter blog title"
                    />
                  </div>

                  <div className="form-group">
                    <label>Slug (URL) <span style={{ fontSize: '12px', color: '#666' }}>(auto-generated if empty)</span></label>
                    <input
                      type="text"
                      value={blogData.slug}
                      onChange={(e) => setBlogData({ ...blogData, slug: e.target.value })}
                      placeholder="blog-url-slug"
                    />
                  </div>

                  <div className="form-group">
                    <label>Excerpt (Short Description)</label>
                    <textarea
                      value={blogData.excerpt}
                      onChange={(e) => setBlogData({ ...blogData, excerpt: e.target.value })}
                      placeholder="Brief description of the blog post..."
                      rows="3"
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Category *</label>
                      <select
                        value={blogData.category}
                        onChange={(e) => setBlogData({ ...blogData, category: e.target.value })}
                      >
                        <option value="JEE">JEE</option>
                        <option value="NEET">NEET</option>
                        <option value="Study Tips">Study Tips</option>
                        <option value="Career Guidance">Career Guidance</option>
                        <option value="Exam Strategy">Exam Strategy</option>
                        <option value="Success Stories">Success Stories</option>
                        <option value="News">News</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Author</label>
                      <input
                        type="text"
                        value={blogData.author}
                        onChange={(e) => setBlogData({ ...blogData, author: e.target.value })}
                        placeholder="Author name"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Tags</label>
                    <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                      <input
                        type="text"
                        value={tagInput}
                        onChange={(e) => setTagInput(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                        placeholder="Add a tag and press Enter"
                        style={{ flex: 1 }}
                      />
                      <button 
                        type="button" 
                        className="btn btn-primary btn-sm" 
                        onClick={handleAddTag}
                      >
                        Add Tag
                      </button>
                    </div>
                    {blogData.tags.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        {blogData.tags.map((tag, idx) => (
                          <span 
                            key={idx} 
                            className="badge" 
                            style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: '5px',
                              cursor: 'pointer'
                            }}
                            onClick={() => handleRemoveTag(tag)}
                          >
                            {tag} ×
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="form-group">
                    <label>Featured Image</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                    />
                    {blogData.featuredImage && (
                      <img
                        src={blogData.featuredImage}
                        alt="Preview"
                        style={{ width: '200px', marginTop: '10px', borderRadius: '8px' }}
                      />
                    )}
                  </div>

                  <div className="form-group">
                    <label>Content *</label>
                    <textarea
                      value={blogData.content}
                      onChange={(e) => setBlogData({ ...blogData, content: e.target.value })}
                      placeholder="Write your blog content here... (You can use Markdown formatting)"
                      rows="15"
                      style={{ fontFamily: 'monospace' }}
                    />
                  </div>

                  <div className="form-group">
                    <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <input
                        type="checkbox"
                        checked={blogData.isPublished}
                        onChange={(e) => setBlogData({ ...blogData, isPublished: e.target.checked })}
                      />
                      Publish immediately
                    </label>
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button className="btn btn-outline" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button className="btn btn-success" onClick={handleCreateBlog}>
                  {editingBlog ? 'Update Blog' : 'Create Blog'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ManageBlogs;
